import React, { useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import { LoadingButton } from "@mui/lab";
import FileUploader from "app/widgets/Shared/FileUploader/FileUploader";
import companyServices from "app/services/company-services";
import userServices from "app/services/user-services";
import { downloadBuffer } from "app/utils/appHelpers";
import { useSnackbar } from "notistack";
import moment from "moment";

const COUNT_LABELS = {
    companies: "Compañías",
    centres: "Bodegas",
    transport_units: "Unidades de transporte",
    vehicles: "Vehículos",
    customers: "Clientes",
    users: "Usuarios",
    plugins: "Plugins",
};

// Las dos formas de dar de alta un cliente nuevo. Se navegan con las flechas y
// viven en el mismo modal porque son la misma decision -"voy a crear una
// compania"- tomada con mas o menos datos a mano.
const MODES = [
    { key: "manual", label: "A mano" },
    { key: "excel", label: "Desde un Excel" },
];

const emptyCompany = () => ({
    name: "",
    tin: "",
    contact_email: "",
    contact_phone: "",
    whatsapp_number: "",
});

// Solo nombre y direccion: el codigo se genera solo y el recorrido de picking
// queda "por codigo", igual que en el Excel cuando esas columnas vienen vacias.
const emptyCentre = () => ({
    name: "",
    address: "",
});

const isEmptyCentre = (centre) => !centre.name.trim() && !centre.address.trim();

const isCompleteCentre = (centre) => centre.name.trim().length > 0 && centre.address.trim().length > 0;

const emptyUser = () => ({
    email: "",
    first_name: "",
    last_name: "",
    phone_primary: "",
    profiles: [],
});

// Una fila en blanco no es un usuario a medio llenar: es una fila que quedó de
// mas. Se descarta antes de enviar en vez de hacerla fallar por falta de email.
const isEmptyUser = (user) => (
    !user.email.trim()
    && !user.first_name.trim()
    && !user.last_name.trim()
    && !user.phone_primary.trim()
    && user.profiles.length === 0
);

const CompanyImportModal = ({
    open = false,
    onClose = () => {},
    onImported = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [modeIndex, setModeIndex] = useState(0);
    const [filesToImport, setFilesToImport] = useState([]);
    const [importing, setImporting] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [importErrors, setImportErrors] = useState([]);
    const [result, setResult] = useState(null);
    const [newCompany, setNewCompany] = useState(emptyCompany);
    const [newCentres, setNewCentres] = useState(() => [emptyCentre()]);
    const [newUsers, setNewUsers] = useState(() => [emptyUser()]);
    const [profileOptions, setProfileOptions] = useState([]);

    const mode = MODES[modeIndex];
    const isManual = mode.key === "manual";

    const handleClose = () => {
        setModeIndex(0);
        setFilesToImport([]);
        setImporting(false);
        setImportErrors([]);
        setResult(null);
        setNewCompany(emptyCompany());
        setNewCentres([emptyCentre()]);
        setNewUsers([emptyUser()]);
        onClose();
    };

    // Los perfiles salen del mismo endpoint que usa la pantalla de Usuarios, para
    // que la lista sea una sola y no una copia que se desactualiza. Se piden al
    // abrir el modal y no al cambiar de modo: asi el formulario nunca aparece con
    // el selector de perfiles vacio.
    useEffect(() => {
        if (!open || profileOptions.length > 0) return;

        let cancelled = false;
        userServices.getEditOptions()
            .then((data) => {
                if (cancelled) return;
                setProfileOptions(data?.profile_names || []);
            })
            .catch(() => {
                // El interceptor ya muestra el error; sin lista igual se puede
                // cargar el Excel, que es la otra mitad del modal.
            });

        return () => { cancelled = true; };
    }, [open, profileOptions.length]);

    const goToMode = (delta) => {
        setImportErrors([]);
        setModeIndex((prev) => (prev + delta + MODES.length) % MODES.length);
    };

    const downloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);
            const data = await companyServices.downloadOnboardingTemplate();
            downloadBuffer(data, `carga_cliente_nuevo_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        } catch (_error) {
            // El interceptor muestra el mensaje generico.
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const importFile = async () => {
        if (!filesToImport.length) return;

        try {
            setImporting(true);
            setImportErrors([]);

            const response = await companyServices.importOnboarding(filesToImport[0]);
            setResult(response);
            enqueueSnackbar(response.message || "Importación completada", { variant: "success" });
            onImported();
        } catch (error) {
            const details = error?.response?.data?.details;
            if (Array.isArray(details) && details.length > 0) {
                setImportErrors(details);
            }
        } finally {
            setImporting(false);
        }
    };

    const createCompany = async () => {
        try {
            setImporting(true);
            setImportErrors([]);

            const response = await companyServices.createOnboardingCompany({
                company: newCompany,
                centres: newCentres.filter((centre) => !isEmptyCentre(centre)).map((centre) => ({
                    name: centre.name.trim(),
                    address: centre.address.trim(),
                })),
                users: newUsers.filter((user) => !isEmptyUser(user)).map((user) => ({
                    email: user.email.trim(),
                    first_name: user.first_name.trim(),
                    last_name: user.last_name.trim(),
                    phone_primary: user.phone_primary.trim(),
                    profiles: user.profiles.map((profile) => profile.name),
                })),
            });

            setResult(response);
            enqueueSnackbar(response.message || "Compañía creada", { variant: "success" });
            onImported();
        } catch (error) {
            const details = error?.response?.data?.details;
            if (Array.isArray(details) && details.length > 0) {
                setImportErrors(details);
            }
        } finally {
            setImporting(false);
        }
    };

    const updateCompanyField = (field) => (event) => {
        setNewCompany((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const updateCentreField = (index, field, value) => {
        setNewCentres((prev) => prev.map((centre, i) => (i === index ? { ...centre, [field]: value } : centre)));
    };

    const addCentre = () => setNewCentres((prev) => [...prev, emptyCentre()]);

    const removeCentre = (index) => setNewCentres((prev) => (
        prev.length === 1 ? [emptyCentre()] : prev.filter((_centre, i) => i !== index)
    ));

    const updateUserField = (index, field, value) => {
        setNewUsers((prev) => prev.map((user, i) => (i === index ? { ...user, [field]: value } : user)));
    };

    const addUser = () => setNewUsers((prev) => [...prev, emptyUser()]);

    const removeUser = (index) => setNewUsers((prev) => (
        prev.length === 1 ? [emptyUser()] : prev.filter((_user, i) => i !== index)
    ));

    // Sin bodega la compania no puede operar, asi que el boton espera al menos una
    // completa. Una a medio llenar no bloquea: la rechaza el backend nombrandola.
    const canCreate = newCompany.name.trim().length > 0 && newCentres.some(isCompleteCentre);

    const credentials = result?.user_credentials || [];

    const copyCredentials = () => {
        const text = credentials.map((c) => `${c.email}\t${c.password}`).join("\n");
        navigator.clipboard?.writeText(text)
            .then(() => enqueueSnackbar("Copiado al portapapeles", { variant: "success" }))
            .catch(() => enqueueSnackbar("No fue posible copiar", { variant: "error" }));
    };

    const renderModeSwitcher = () => (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
            <IconButton size="small" onClick={() => goToMode(-1)} aria-label="Forma anterior">
                <ChevronLeftRoundedIcon />
            </IconButton>
            <Box sx={{ textAlign: "center", minWidth: 190 }}>
                <Typography variant="subtitle1" fontWeight={600}>{mode.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {modeIndex + 1} de {MODES.length}
                </Typography>
            </Box>
            <IconButton size="small" onClick={() => goToMode(1)} aria-label="Forma siguiente">
                <ChevronRightRoundedIcon />
            </IconButton>
        </Stack>
    );

    const renderExcelPanel = () => (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Descarga el template, llénalo con los datos del cliente nuevo y súbelo acá.
                La compañía se crea a partir de la hoja <strong>compania</strong>, junto con sus
                bodegas, clientes y usuarios. Los vehículos se agregan después desde su
                propia pantalla.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Solo <strong>compania</strong> y <strong>bodegas</strong> son obligatorias, y bodegas
                tiene que traer al menos una. Si una fila
                tiene un error no se carga nada del archivo: se corrige y se vuelve a subir.
            </Typography>

            <FileUploader
                acceptFamily="spreadsheet"
                maxFiles={1}
                onFileSelection={setFilesToImport}
            />
        </>
    );

    const renderManualPanel = () => (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se crean la compañía, sus bodegas y sus usuarios. Necesita al menos una
                bodega. Los clientes y la flota se cargan después desde sus propias pantallas.
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Compañía</Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={7}>
                    <TextField
                        fullWidth
                        required
                        size="small"
                        label="Nombre"
                        value={newCompany.name}
                        onChange={updateCompanyField("name")}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="RUT"
                        value={newCompany.tin}
                        onChange={updateCompanyField("tin")}
                    />
                </Grid>
                <Grid item xs={12} sm={7}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Email de contacto"
                        value={newCompany.contact_email}
                        onChange={updateCompanyField("contact_email")}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Teléfono"
                        value={newCompany.contact_phone}
                        onChange={updateCompanyField("contact_phone")}
                    />
                </Grid>
                <Grid item xs={12} sm={7}>
                    <TextField
                        fullWidth
                        size="small"
                        label="WhatsApp"
                        value={newCompany.whatsapp_number}
                        onChange={updateCompanyField("whatsapp_number")}
                    />
                </Grid>
            </Grid>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Bodegas *</Typography>
                <Button size="small" startIcon={<AddBusinessOutlinedIcon />} onClick={addCentre}>
                    Agregar bodega
                </Button>
            </Stack>

            {newCentres.map((centre, index) => (
                <Box
                    key={`nueva-bodega-${index}`}
                    sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Bodega {index + 1}
                        </Typography>
                        <Tooltip arrow title="Quitar esta bodega">
                            <IconButton size="small" onClick={() => removeCentre(index)}>
                                <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Nombre"
                                value={centre.name}
                                onChange={(e) => updateCentreField(index, "name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label="Dirección"
                                placeholder="Calle 123, Comuna, Ciudad"
                                value={centre.address}
                                onChange={(e) => updateCentreField(index, "address", e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
            ))}

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, mt: 2 }}>
                <Typography variant="subtitle2">Usuarios</Typography>
                <Button size="small" startIcon={<PersonAddAltOutlinedIcon />} onClick={addUser}>
                    Agregar usuario
                </Button>
            </Stack>

            {newUsers.map((user, index) => (
                <Box
                    key={`nuevo-usuario-${index}`}
                    sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Usuario {index + 1}
                        </Typography>
                        <Tooltip arrow title="Quitar este usuario">
                            <IconButton size="small" onClick={() => removeUser(index)}>
                                <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Email"
                                value={user.email}
                                onChange={(e) => updateUserField(index, "email", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Teléfono"
                                value={user.phone_primary}
                                onChange={(e) => updateUserField(index, "phone_primary", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nombre"
                                value={user.first_name}
                                onChange={(e) => updateUserField(index, "first_name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Apellido"
                                value={user.last_name}
                                onChange={(e) => updateUserField(index, "last_name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={profileOptions}
                                getOptionLabel={(option) => option?.human_name || option?.name || ""}
                                isOptionEqualToValue={(option, value) => option?.name === value?.name}
                                value={user.profiles}
                                onChange={(_e, value) => updateUserField(index, "profiles", value)}
                                renderInput={(params) => <TextField {...params} label="Perfiles" />}
                            />
                        </Grid>
                    </Grid>
                </Box>
            ))}
        </>
    );

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="h2" color="primary">Agregar compañía</Typography>
                    {!result && !isManual && (
                        <LoadingButton
                            loading={downloadingTemplate}
                            variant="contained"
                            color="success"
                            size="small"
                            endIcon={<DownloadOutlinedIcon />}
                            disabled={downloadingTemplate}
                            loadingPosition="end"
                            onClick={downloadTemplate}
                        >
                            {downloadingTemplate ? "Descargando" : "Template"}
                        </LoadingButton>
                    )}
                </Stack>
            </DialogTitle>

            <DialogContent>
                {!result && (
                    <>
                        {renderModeSwitcher()}
                        {isManual ? renderManualPanel() : renderExcelPanel()}
                    </>
                )}

                {importErrors.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Errores detectados. No se cargó nada.
                        </Typography>
                        <List disablePadding>
                            {importErrors.map((errorMessage, index) => (
                                <ListItem key={`onboarding-error-${index}`} sx={{ py: 0 }}>
                                    <Typography variant="body2">- {errorMessage}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}

                {result && (
                    <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Se creó <strong>{result.company_name}</strong> (ID: {result.company_id}).
                        </Alert>

                        <Stack direction="row" flexWrap="wrap" spacing={0} sx={{ mb: 2 }}>
                            {Object.entries(result.counts || {}).map(([key, count]) => (
                                <Typography key={key} variant="body2" sx={{ mr: 3 }}>
                                    {COUNT_LABELS[key] || key}: <strong>{count}</strong>
                                </Typography>
                            ))}
                        </Stack>

                        {credentials.length > 0 && (
                            <>
                                <Divider sx={{ mb: 2 }} />
                                <Alert severity="warning" sx={{ mb: 1.5 }}>
                                    Estas claves se muestran una sola vez y no se enviaron por correo.
                                    Cópialas antes de cerrar.
                                </Alert>
                                <Button
                                    size="small"
                                    startIcon={<ContentCopyOutlinedIcon />}
                                    onClick={copyCredentials}
                                    sx={{ mb: 1 }}
                                >
                                    Copiar todas
                                </Button>
                                <Box sx={{ overflowX: "auto" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Clave temporal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {credentials.map((credential) => (
                                                <TableRow key={credential.email}>
                                                    <TableCell>{credential.email}</TableCell>
                                                    <TableCell sx={{ fontFamily: "monospace" }}>
                                                        {credential.password}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>{result ? "Listo" : "Cerrar"}</Button>
                {!result && (
                    <LoadingButton
                        loading={importing}
                        variant="contained"
                        color="success"
                        size="small"
                        endIcon={<SaveOutlinedIcon />}
                        disabled={isManual ? !canCreate : !filesToImport.length}
                        loadingPosition="end"
                        onClick={isManual ? createCompany : importFile}
                    >
                        {importing
                            ? (isManual ? "Creando" : "Importando")
                            : (isManual ? "Crear" : "Importar")}
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CompanyImportModal;
